import { useCallback } from 'react';

import { Box, Skeleton, useTheme } from '@mui/material';
import { GridSortModel, GridToolbarProps, GridValidRowModel } from '@mui/x-data-grid';
import {
    DataGridPro,
    GridCallbackDetails,
    GridColDef,
    GridEventListener,
    GridFilterModel,
    GridPinnedRowsProp,
    GridRowIdGetter,
    GridRowParams,
    GridRowProps,
    GridRowSelectionModel,
} from '@mui/x-data-grid-pro';

import { DEFAULT_BORDER } from 'src/constants';
import {
    disableCopyEventHandler,
    disableCutEventHandler,
} from 'src/hooks/useEventListener/eventHandlers';
import { DEFAULT_PAGE_SIZE_OPTIONS } from 'src/lib/pagination';

import CustomMultiFilterPanel from './components/CustomFilter';
import CustomFooter from './components/CustomFooter';
import CustomToolbar, {
    AddFilterButtonConfig,
    AddRecordButtonConfig,
    DeleteModeConfig,
} from './components/CustomToolbar';

export type NPIDataGridRow = GridRowProps;
export type NPIDataGridColDef = GridColDef;
export type NPIDataGridGetRowIdGetter = GridRowIdGetter;

type NPIDataGridProps = {
    rows: NPIDataGridRow[];
    columns: NPIDataGridColDef[];
    hiddenColumns?: string[];
    getRowId: NPIDataGridGetRowIdGetter;
    dataTestId: string;
    title?: string;
    pagination?: {
        isEnabled?: boolean;
        pageSizeOptions?: number[];
        mode?: 'client' | 'server';
        page?: number;
        pageSize?: number;
        totalRows?: number;
        onPageChange?: (page: number, pageSize: number) => void;
    };
    filtering?: {
        isEnabled: boolean;
    };
    rowReordering?: {
        isEnabled: boolean;
    };
    addRecordButtonConfig?: AddRecordButtonConfig;
    addFilterButtonConfig?: AddFilterButtonConfig;
    pinnedRow?: GridPinnedRowsProp<NPIDataGridRow>;
    rowOrderChange?: GridEventListener<'rowOrderChange'>;
    maxHeight?: string;
    disableCopyCut?: boolean;
    serverSideFiltering?: {
        onFilterChange: (model: GridFilterModel) => void;
        filterModel?: GridFilterModel;
    } | null;
    key: string;
    serverSideSorting?: {
        onSortChange: (model: GridSortModel, details: GridCallbackDetails) => void;
        sortModel: GridSortModel;
    } | null;
    handleReset?: () => void;
    handleView?: (row: GridValidRowModel) => void;
    onSelectionChange?: (ids: (string | number)[]) => void;
    deleteModeConfig?: DeleteModeConfig;
    selectedRowIds?: (string | number)[];
    overWriteDefaultStyles?: Record<string, unknown>;
    loading?: boolean;
};

export function NPIDataGrid({
    rows,
    columns,
    hiddenColumns = [],
    getRowId,
    title,
    pagination = {
        isEnabled: true,
        mode: 'client',
    },
    filtering = {
        isEnabled: true,
    },
    dataTestId = `${title ? `${title}-` : ''}data-grid`,
    rowReordering = { isEnabled: false },
    addRecordButtonConfig,
    addFilterButtonConfig,
    pinnedRow,
    rowOrderChange,
    maxHeight,
    disableCopyCut = true,
    serverSideFiltering = null,
    key = 'npi-data-grid',
    serverSideSorting = null,
    handleReset,
    handleView,
    onSelectionChange,
    deleteModeConfig,
    selectedRowIds,
    overWriteDefaultStyles = {},
    loading = false,
}: NPIDataGridProps) {
    const theme = useTheme();

    const {
        page: currentPage = 0,
        pageSize: currentPageSize,
        totalRows: totalRowsFromPagination,
        isEnabled: isPaginationEnabled = true,
        mode: paginationMode = 'client',
        onPageChange,
        pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    } = pagination;

    // Pagination
    const handleOnPaginationModelChange = useCallback(
        async (model: { pageSize: number; page: number }) => {
            const isServerPagination = paginationMode === 'server';
            if (isServerPagination && onPageChange) {
                onPageChange(model.page, model.pageSize);
            }
        },
        [onPageChange, paginationMode]
    );

    // Filter
    const filterPanelComponent = () => {
        return (
            <CustomMultiFilterPanel
                columns={columns}
                onFilterModelChange={async (model: GridFilterModel) => {
                    if (serverSideFiltering) {
                        serverSideFiltering.onFilterChange(model);
                    }
                }}
                filterState={serverSideFiltering.filterModel}
            />
        );
    };

    // Sort
    const handleSortModelChange = async (model: GridSortModel, details: GridCallbackDetails) => {
        if (deleteModeConfig?.isActive) {
            onSelectionChange?.([]);
        }

        if (serverSideSorting) {
            serverSideSorting.onSortChange(model, details);
        }
    };

    const selectRecordForDeletion = (params: GridRowParams, event: unknown) => {
        (event as { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
        const id = params.row.id as string | number;
        const currentlySelected = selectedRowIds ?? [];
        const exists = currentlySelected.includes(id);
        const next = exists
            ? currentlySelected.filter((x: string | number) => {
                  return x !== id;
              })
            : [...currentlySelected, id];

        if (onSelectionChange) {
            onSelectionChange(next);
        }
    };

    const handleFilterModelChange = useCallback(
        (model: GridFilterModel) => {
            if (deleteModeConfig?.isActive) {
                onSelectionChange?.([]);
            }

            if (model.items.length === 0 && serverSideFiltering) {
                serverSideFiltering.onFilterChange(model);
            }
        },
        [deleteModeConfig?.isActive, onSelectionChange, serverSideFiltering]
    );

    const loadingOverlay = () => {
        return (
            <Skeleton
                variant="rounded"
                animation="wave"
                placeholder={`Loading Data`}
                width={'100%'}
                height={'400px'}
            />
        );
    };
    return (
        <Box
            sx={{
                ...DEFAULT_BORDER,
                width: '100%',
                ...overWriteDefaultStyles,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight,
                }}
                onCopy={disableCopyCut ? disableCopyEventHandler : null}
                onCut={disableCopyCut ? disableCutEventHandler : null}
            >
                <DataGridPro
                    key={`${key}-${dataTestId}`}
                    data-testid={dataTestId}
                    columnVisibilityModel={
                        hiddenColumns?.reduce(
                            (acc: Record<string, boolean>, field: string) => {
                                acc[field] = false;
                                return acc;
                            },
                            {} as Record<string, boolean>
                        ) ?? {}
                    }
                    sx={{
                        '.MuiDataGrid-footerContainer': {
                            height: '100%',
                        },
                        '.MuiDataGrid-toolbarContainer': {
                            height: '100%',
                        },
                        '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            padding: '8px',
                            backgroundColor: 'transparent !important',
                            border: 'none',
                            cursor: handleView ? 'pointer' : 'default',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: 'transparent !important',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'transparent !important',
                        },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                            {
                                outline: 'none',
                            },
                        '& .MuiDataGrid-root, .MuiDataGrid-row:hover': {
                            backgroundColor: theme.palette.action.tableRowHover,
                        },
                        // Prevent DataGrid from applying a different background for selected rows
                        '& .MuiDataGrid-row.Mui-selected:nth-of-type(odd)': {
                            backgroundColor: 'transparent !important',
                        },
                        '& .MuiDataGrid-row.Mui-selected:nth-of-type(even)': {
                            backgroundColor: '#f6f6f6 !important',
                        },
                    }}
                    initialState={{
                        pagination: isPaginationEnabled
                            ? {
                                  paginationModel: {
                                      pageSize: currentPageSize ?? pageSizeOptions[0],
                                      page: currentPage,
                                  },
                              }
                            : null,
                    }}
                    pagination={isPaginationEnabled}
                    paginationMode={paginationMode}
                    rowCount={totalRowsFromPagination ?? (rows ? rows.length : 0)}
                    paginationModel={
                        paginationMode === 'server'
                            ? { page: currentPage, pageSize: currentPageSize ?? pageSizeOptions[0] }
                            : undefined
                    }
                    rows={rows ?? []}
                    columns={columns}
                    getRowId={getRowId}
                    disableColumnFilter={
                        addFilterButtonConfig !== undefined || !filtering?.isEnabled
                    }
                    getRowHeight={() => {
                        return 'auto';
                    }}
                    disableDensitySelector
                    disableColumnPinning
                    disableColumnSelector
                    {...(serverSideFiltering && {
                        filterMode: 'server',
                        onFilterModelChange: handleFilterModelChange,
                        filterModel: serverSideFiltering?.filterModel,
                    })}
                    onPaginationModelChange={handleOnPaginationModelChange}
                    {...(serverSideSorting && {
                        sortingMode: 'server',
                        sortModel: serverSideSorting.sortModel,
                        onSortModelChange: handleSortModelChange,
                    })}
                    checkboxSelection={Boolean(deleteModeConfig?.isActive)}
                    checkboxSelectionVisibleOnly={Boolean(deleteModeConfig?.isActive)}
                    rowSelectionModel={selectedRowIds ?? []}
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={(model: GridRowSelectionModel) => {
                        if (onSelectionChange) {
                            onSelectionChange(model as (string | number)[]);
                        }
                    }}
                    onRowClick={(params: GridRowParams, event: unknown) => {
                        if (deleteModeConfig?.isActive) {
                            selectRecordForDeletion(params, event);
                            return;
                        }

                        if (handleView) {
                            handleView(params.row);
                        }
                    }}
                    loading={loading}
                    slots={{
                        toolbar: CustomToolbar,
                        loadingOverlay,
                        ...(serverSideFiltering !== null && {
                            filterPanel: filterPanelComponent,
                        }),
                        ...(pagination?.isEnabled && {
                            footer: () => {
                                return <CustomFooter pageSizeOptions={pageSizeOptions} />;
                            },
                        }),
                        noRowsOverlay: () => {
                            return (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        padding: '20px',
                                    }}
                                >
                                    No Records Found.
                                </Box>
                            );
                        },
                    }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            title,
                            addRecordButtonConfig,
                            addFilterButtonConfig,
                            serverSideFilteringEnabled: serverSideFiltering !== null,
                            handleReset,
                            deleteModeConfig,
                        } as Partial<GridToolbarProps>,
                    }}
                    pageSizeOptions={pageSizeOptions}
                    rowReordering={rowReordering?.isEnabled}
                    pinnedColumns={{ right: ['actions'] }}
                    pinnedRows={pinnedRow}
                    onRowOrderChange={rowOrderChange}
                />
            </div>
        </Box>
    );
}
